<template>
  <v-menu
    v-model="showMenu"
    :close-on-content-click="false"
    :close-on-click="closeOnClick"
    min-width="681"
    :nudge-top="20"
    transition="scale-transition"
    left
    offset-y
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        :value="inputLabel"
        :hint="hint"
        :dense="dense"
        :outlined="outlined"
        :persistent-hint="persistentHint"
        :hide-details="hideDetails"
        :error="errorMessages.length > 0"
        :error-messages="errorMessages"
        readonly
        :prepend-icon="prependIcon"
        :label="label"
        v-on="on"
      />
    </template>

    <v-card>
      <v-card-text class="black--text pa-2">
        <div class="pa-0 d-flex horizontal-scrollbar">
          <div>
            <v-card-actions class="primaryLight align-end">
              <v-text-field
                v-model="startDate"
                readonly
                hide-details
                label="Start Date"
                class="mx-2"
              />
              <v-text-field
                v-model="endDate"
                readonly
                hide-details
                label="End Date"
                class="mx-2"
              />
            </v-card-actions>

            <div class="d-flex">
              <v-date-picker
                v-model="startDate"
                no-title
                :allowed-dates="allowedStartDates"
                color="primary"
              />

              <v-date-picker
                v-model="endDate"
                no-title
                :allowed-dates="allowedEndDates"
                color="primary"
                event-color="primary lighten-1"
              />
            </div>
          </div>

          <div class="d-flex flex-column align-center justify-space-between">
            <v-list class="pa-0">
              <template v-for="(choice, index) in filterChoices">
                <v-list-item
                  :key="index"
                  style="min-height: 36px !important;"
                >
                  <v-btn
                    block
                    :outlined="appliedFilter !== choice"
                    color="primary"
                    small
                    depressed
                    class="my-1 text-capitalize"
                    @click="setDateAndAppliedFilter(choice)"
                    v-text="choice"
                  />
                </v-list-item>
              </template>
            </v-list>

            <v-btn
              color="success"
              depressed
              class="mx-auto my-1"
              @click="applyFilter"
            >
              <span class="mx-2">Apply</span>
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider />
    </v-card>
  </v-menu>
</template>

<script>
import moment from 'moment';
import { enumerateDaysBetweenDates } from '@/utils/misc';

export default {
  props: {
    value: {
      type: Object,
      default: undefined,
    },
    default: {
      type: String,
      default: 'This Month',
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [String, Array],
      default() {
        return [];
      },
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Filter by Date',
    },
    allowedDates: {
      type: [Function, Object, Array],
      default: undefined,
    },
  },
  data() {
    return {
      inputLabel: this.default,
      filterChoices: [
        'Today',
        'Yesterday',
        'This Week',
        'Last 7 Days',
        'Last 30 Days',
        'This Month',
        'Last Month',
      ],
      appliedFilter: this.default,
      startDate: '',
      endDate: '',
      dateRangeMap: {
        'All Time': {
          startDate: '',
          endDate: '',
        },
        'Till Date': {
          startDate: '',
          endDate: moment().format('YYYY-MM-DD'),
        },
        Today: {
          startDate: moment().format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
        Yesterday: {
          startDate: moment().subtract(1, 'days').format('YYYY-MM-DD'),
          endDate: moment().subtract(1, 'days').format('YYYY-MM-DD'),
        },
        'Last 7 Days': {
          startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
        'Last 30 Days': {
          startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
        'This Month': {
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD'),
        },
        'Last Month': {
          startDate: moment()
            .startOf('month')
            .subtract(1, 'month')
            .format('YYYY-MM-DD'),
          endDate: moment()
            .endOf('month')
            .subtract(1, 'month')
            .format('YYYY-MM-DD'),
        },
        'This Year': {
          startDate: '',
          endDate: '',
        },
        'Last Year': {
          startDate: '',
          endDate: '',
        },
        'This Week': {
          startDate: moment().startOf('week').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
        'Last Week': {
          startDate: moment()
            .startOf('week')
            .subtract(1, 'week')
            .format('YYYY-MM-DD'),
          endDate: moment()
            .startOf('week')
            .subtract(1, 'days')
            .format('YYYY-MM-DD'),
        },
      },
      showMenu: false,
      closeOnClick: true,
    };
  },
  computed: {
    // TODO: IT's not being used anymore and can be removed
    highlightenStartDates() {
      const startDate = moment(this.startDate)
        .add('1', 'day')
        .format('YYYY-MM-DD');
      const endDate = moment(this.endDate).format('YYYY-MM-DD');
      return this.enumerateDaysBetweenDates(startDate, endDate);
    },

    // TODO: IT's not being used anymore and can be removed
    highlightenEndDates() {
      const startDate = moment(this.startDate).format('YYYY-MM-DD');
      const endDate = moment(this.endDate)
        .subtract('1', 'day')
        .format('YYYY-MM-DD');
      return this.enumerateDaysBetweenDates(startDate, endDate);
    },
  },
  watch: {
    value(val) {
      if (val.startDate !== this.startDate || val.endDate !== this.endDate) {
        this.updateDisplayValue(val);
      }
    },
    startDate(val) {
      this.updateDisplayValue({ startDate: val, endDate: this.endDate });
      this.closeOnClick = !this.changeOccurred;
    },
    endDate(val) {
      this.updateDisplayValue({ startDate: this.startDate, endDate: val });
      this.closeOnClick = !this.changeOccurred;
    },
    appliedFilter() {
      this.$emit('update', { dateStatus: this.appliedFilter });
    },
  },
  created() {
    this.updateDisplayValue(this.value);
    this.inputLabel = this.appliedFilter;
  },
  methods: {
    enumerateDaysBetweenDates,
    allowedStartDates(date) {
      return moment(date)
        .subtract('1', 'day')
        .isBefore(moment(this.endDate).format('YYYY-MM-DD'));
    },
    allowedEndDates(date) {
      const isNotBeforeStartDate = !moment(date).isBefore(
        moment(this.startDate).format('YYYY-MM-DD'),
      );

      return isNotBeforeStartDate;
    },
    applyFilter() {
      this.emitDateRange();
      this.setAppliedFilter();
      this.inputLabel = this.appliedFilter;
      this.showMenu = false;
      this.closeOnClick = true;
    },
    emitDateRange() {
      const payload = {
        startDate: this.startDate,
        endDate: this.endDate,
      };
      this.$emit('input', payload);
    },
    setAppliedFilter() {
      if (!this.filterChoices.includes(this.appliedFilter)) {
        this.appliedFilter = `${this.startDate || 'N/A'}  -  ${this.endDate || 'N/A'}`;
      }
    },
    setDateAndAppliedFilter(choice) {
      this.startDate = this.dateRangeMap[choice].startDate;
      this.endDate = this.dateRangeMap[choice].endDate;
      this.appliedFilter = choice;
    },
    updateDisplayValue(val) {
      this.startDate = val.startDate;
      this.endDate = val.endDate;
      /* eslint-disable  no-restricted-syntax */
      for (const choice of this.filterChoices) {
        const filter = this.dateRangeMap[choice];
        if (
          filter.startDate === this.startDate
          && filter.endDate === this.endDate
        ) {
          this.appliedFilter = choice;
          return;
        }
      }
      this.appliedFilter = `${this.startDate || 'N/A'}  -  ${this.endDate || 'N/A'}`;
    },
  },
};
</script>
